import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import axiosInstance from 'pages/Utility/axiosInstance';
import { ClipLoader } from 'react-spinners';
import { useSelector } from 'react-redux';

const CreateChapter = ({ visible, handleClose, initialData = {}, onSubmit, courseOptions }) => {
  const [modules, setModules] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(initialData?.moduleId?.courseId || null);
  const loading = useSelector(state => state.Chapter.loading);
  // Fetch modules when a course is selected
  const fetchModulesByCourse = async (courseId) => {
    if (!courseId) return;
    try {
      const response = await axiosInstance.get(`V1/modules/dropdown/${courseId}`);
      setModules(response?.data?.data|| []);
    } catch (error) {
      console.error(error);
      setModules([]);
    }
  };

  useEffect(() => {
    if (selectedCourse) {
      fetchModulesByCourse(selectedCourse);
    } else {
      setModules([]);
    }
  }, [selectedCourse]);

  useEffect(()=>{
    if(initialData){
      setSelectedCourse(initialData?.moduleId?.courseId);
    }
  },[initialData])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: initialData?.moduleId?.courseId || '',
      moduleId: initialData?.moduleId?._id || '',
      title: initialData?.title || '',
      orderIndex: initialData?.orderIndex || '',
    },
    validationSchema: Yup.object({
      courseId: Yup.string().required('Course is required'),
      moduleId: Yup.string().required('Module is required'),
      title: Yup.string().required('Title is required'),
      orderIndex: Yup.number()
           .typeError("Order must be a number")
           .required("Order is required")
           .min(0, "Order must be a non-negative number"),  
    }),
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        orderIndex: values.orderIndex,
        moduleId: values.moduleId,
      };

      if (!initialData?._id) {
        onSubmit(payload, onClose);
      } else {
        onSubmit(initialData._id, payload, onClose);
      }
    },
  });

  const onClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Modal
      title={initialData?._id ? 'Edit Chapter' : 'Create New Chapter'}
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={500}
      className="custom-modal-header p-0"
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">

          {/* Course Select */}
          <div className="col-12">
            <label className="modal-form">
              Course <span className="text-danger">*</span>
            </label>
            <Select
              placeholder="Select course"
              options={courseOptions}
              value={courseOptions.find((opt) => opt.value == formik.values.courseId) || null}
              onChange={(option) => {
                setSelectedCourse(option?.value || '');
                formik.setFieldValue('courseId', option?.value || '');
                formik.setFieldValue('moduleId', ''); // reset module when course changes
              }}
              isClearable
            />
            {formik.touched.courseId && formik.errors.courseId && (
              <span style={{ color: 'red' }}>{formik.errors.courseId}</span>
            )}
          </div>

          {/* Module Select */}
          <div className="col-12">
            <label className="modal-form">
              Module <span className="text-danger">*</span>
            </label>
            <Select
              placeholder="Select module"
              options={modules.map((m) => ({ label: m.title, value: m._id }))}
              value={modules
                .map((m) => ({ label: m.title, value: m._id }))
                .find((opt) => opt.value == formik.values.moduleId) || null}
              onChange={(option) => formik.setFieldValue('moduleId', option?.value || '')}
              isClearable
              isDisabled={!formik.values.courseId}
            />
            {formik.touched.moduleId && formik.errors.moduleId && (
              <span style={{ color: 'red' }}>{formik.errors.moduleId}</span>
            )}
          </div>

          {/* Title */}
          <div className="col-12">
            <label className="modal-form">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control fs-7"
              placeholder="Enter title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
            {formik.touched.title && formik.errors.title && (
              <span style={{ color: 'red' }}>{formik.errors.title}</span>
            )}
          </div>

          {/* Order Index */}
          <div className="col-12">
            <label className="modal-form">
              Order <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className="form-control fs-7"
              placeholder="Enter order index"
              name="orderIndex"
              value={formik.values.orderIndex}
              onChange={formik.handleChange}
              min="1"
            />
            {formik.touched.orderIndex && formik.errors.orderIndex && (
              <span style={{ color: 'red' }}>{formik.errors.orderIndex}</span>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer mt-3">
            <button type="button" className="btn btn-light btn-md" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn btn-primary btn-md ms-3" style={{minWidth:'80px'}} disabled={loading}>
             {loading ? <ClipLoader size={16} color='white' /> : initialData?._id ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

CreateChapter.propTypes = {
  visible: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  courseOptions: PropTypes.array.isRequired,
};

export default CreateChapter;
